import React, { useState, useEffect, useCallback } from 'react';
import { fetchAllMcpOrigins, createMcpOrigin, updateMcpOrigin, deleteMcpOrigin } from '../../../services/api';
import toast from 'react-hot-toast';
import { McpOriginModal } from './McpOriginModal';
import { IoAdd, IoPencil, IoTrash, IoGlobeOutline, IoLockClosedOutline } from 'react-icons/io5';

const McpOriginsManager = ({ userIdToken }) => {
    const [origins, setOrigins] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingOrigin, setEditingOrigin] = useState(null);

    const loadOrigins = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await fetchAllMcpOrigins(userIdToken);
            setOrigins(data);
        } catch (error) {
            toast.error(`Failed to load MCP origins: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [userIdToken]);

    useEffect(() => {
        loadOrigins();
    }, [loadOrigins]);

    const handleOpenCreate = () => {
        setEditingOrigin(null);
        setIsModalOpen(true);
    };

    const handleOpenEdit = (origin) => {
        setEditingOrigin(origin);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this origin? This cannot be undone.")) {
            try {
                await deleteMcpOrigin(userIdToken, id);
                toast.success("Origin deleted successfully.");
                loadOrigins();
            } catch (error) {
                toast.error(`Failed to delete origin: ${error.message}`);
            }
        }
    };

    const handleSave = async (data, id) => {
        try {
            if (id) {
                // Update mode
                await updateMcpOrigin(userIdToken, id, data);
                toast.success("Origin updated successfully!");
            } else {
                // Create mode
                await createMcpOrigin(userIdToken, data);
                toast.success("Origin created successfully!");
            }
            setIsModalOpen(false);
            loadOrigins();
        } catch (error) {
            toast.error(`Save failed: ${error.message}`);
        }
    };

    return (
        <div className="mt-12">
             <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">MCP Connection Management</h2>
                <button onClick={handleOpenCreate} className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                    <IoAdd /> Add New Origin
                </button>
            </div>
            <div className="bg-1 rounded-xl shadow-lg overflow-hidden">
                <div className="p-4 space-y-3">
                    {isLoading ? <p>Loading origins...</p> : origins.map(origin => (
                        <div key={origin.id} className="bg-2 p-4 rounded-lg flex items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <img src={origin.logo_url || 'https://via.placeholder.com/40'} alt={origin.origin_name} className="h-10 w-10 rounded-md object-contain" />
                                <div>
                                    <p className="font-semibold text-p-text">{origin.origin_name}</p>
                                    <p className="text-xs text-s-text">{origin.sse_url}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                {origin.isGlobal ? (
                                    <span className="flex items-center gap-1 bg-green-500/10 text-green-400 text-xs font-medium px-2.5 py-1 rounded-full"><IoGlobeOutline /> Global</span>
                                ) : (
                                    <span className="flex items-center gap-1 bg-gray-500/10 text-gray-400 text-xs font-medium px-2.5 py-1 rounded-full"><IoLockClosedOutline /> Private</span>
                                )}
                                <button onClick={() => handleOpenEdit(origin)} className="p-2 text-s-text hover:text-blue-400"><IoPencil /></button>
                                <button onClick={() => handleDelete(origin.id)} className="p-2 text-s-text hover:text-red-400"><IoTrash /></button>
                            </div>
                        </div>
                    ))}
                    {!isLoading && origins.length === 0 && <p className="text-center text-s-text p-8">No MCP Origins configured. Click 'Add New Origin' to start.</p>}
                </div>
            </div>

            <McpOriginModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSave}
                initialData={editingOrigin}
                userIdToken={userIdToken}
            />
        </div>
    );
};
export default McpOriginsManager;